<template>
  <v-dialog v-model="open" max-width="500" persistent @keydown.esc="$emit('close')">
    <v-card style="border-radius:16px; overflow:hidden;">

      <div class="modal-header">
        <v-icon color="#00BCD4" size="20" class="mr-2">mdi-plus-circle-outline</v-icon>
        <span class="modal-title">Dodaj artikl ručno</span>
        <v-spacer />
        <button class="modal-close" @click="$emit('close')">
          <v-icon size="18">mdi-close</v-icon>
        </button>
      </div>

      <div class="modal-body">

        <!-- Naziv -->
        <v-textarea
          v-model="form.name"
          label="Naziv artikla *"
          outlined dense hide-details
          class="wi-input mb-3"
          rows="2"
          no-resize
          autofocus
        />

        <!-- Količina + Jedinica mjere -->
        <v-row dense class="mb-3">
          <v-col cols="6">
            <v-text-field
              :value="parseFloat(form.quantity).toFixed(2)"
              label="Količina"
              type="number" min="0.001" step="0.001"
              outlined dense hide-details
              class="wi-input"
              @focus="$event.target.select()"
              @blur="v => form.quantity = parseFloat(v.target.value) || 1"
            />
          </v-col>
          <v-col cols="6">
            <v-combobox
              v-model="form.unit"
              :items="units"
              label="Jedinica mjere *"
              outlined dense hide-details
              class="wi-input"
            />
          </v-col>
        </v-row>

        <!-- Maloprodajna cijena + Veleprodajna cijena + PDV -->
        <v-row dense class="mb-1">
          <v-col :cols="registerType === 'MP' ? 8 : 4">
            <v-text-field
              :value="parseFloat(form.mpc).toFixed(2)"
              label="Maloprodajna cijena"
              type="number" min="0" step="0.01"
              outlined dense hide-details
              class="wi-input"
              @focus="$event.target.select()"
              @blur="v => { form.mpc = parseFloat(v.target.value) || 0; onMpcChange() }"
            />
          </v-col>
          <v-col v-if="registerType !== 'MP'" cols="4">
            <v-text-field
              :value="parseFloat(form.vpc).toFixed(2)"
              label="Veleprodajna cijena"
              type="number" min="0" step="0.01"
              outlined dense hide-details
              class="wi-input"
              @focus="$event.target.select()"
              @blur="v => { form.vpc = parseFloat(v.target.value) || 0; onVpcChange() }"
            />
          </v-col>
          <v-col :cols="registerType === 'MP' ? 4 : 4">
            <v-select
              v-model="form.pdv"
              :items="taxOptions"
              item-text="name"
              item-value="rate"
              label="PDV"
              outlined dense hide-details
              class="wi-input"
              @change="onPdvChange"
            />
          </v-col>
        </v-row>

        <!-- Usluga / Materijalno dobro -->
        <!-- KPD šifra — samo za non-MP -->
        <v-row v-if="registerType !== 'MP'" dense class="mb-3">
          <v-col cols="12">
            <v-text-field
              v-model="form.kpdcode"
              label="KPD šifra (neobvezno)"
              outlined dense
              class="wi-input"
              placeholder="xx.xx.xx"
              :error-messages="kpdError"
              @input="onKpdInput"
            />
          </v-col>
        </v-row>

        <div class="wi-type-row">
          <span class="wi-type-label">Vrsta</span>
          <div class="seg-control">
            <button class="seg-btn" :class="{ active: form.type === 'service' }" @click="form.type = 'service'">
              <v-icon size="14" class="mr-1">mdi-tools</v-icon>Usluga
            </button>
            <button class="seg-btn" :class="{ active: form.type === 'goods' }" @click="form.type = 'goods'">
              <v-icon size="14" class="mr-1">mdi-package-variant</v-icon>Materijalno dobro
            </button>
          </div>
        </div>

      </div>

      <div class="modal-footer">
        <button class="btn-cancel" @click="$emit('close')">Odustani</button>
        <v-tooltip :disabled="!validationError" bottom max-width="260" color="white" content-class="wi-tooltip">
          <template v-slot:activator="{ on }">
            <span v-on="on">
              <button class="btn-confirm" :disabled="!!validationError || saving" @click="onConfirm">
                <v-icon size="16" color="white" class="mr-1">mdi-check</v-icon>
                Dodaj u košaricu
              </button>
            </span>
          </template>
          <div style="font-size:12px;color:#1e293b;padding:10px 12px;line-height:1.6;white-space:pre-line;">{{ validationError }}</div>
        </v-tooltip>
      </div>

    </v-card>
  </v-dialog>
</template>

<script>
import { v4 as uuidv4 } from 'uuid'
import state from '@/state'

export default {
  name: 'ShopWriteInModal',

  props: {
    open: { type: Boolean, default: false },
    registerType: { type: String, default: 'MP' }
  },

  data () {
    return {
      form: this.emptyForm(),
      kpdError: '',
      taxOptions: [
        { name: 'PDV 25%', rate: 0.25 },
        { name: 'PDV 13%', rate: 0.13 },
        { name: 'PDV 5%', rate: 0.05 },
        { name: 'PDV 0%', rate: 0 }
      ],
      units: ['kom', 'kg', 'l', 'm', 'm²', 'm³', 'h', 'usluga', 'par', 'set']
    }
  },

  computed: {
    currency () { return state.getCurrentCompany()?.currency || 'EUR' },
    validationError () {
      const reasons = []
      if (!this.form.name || !this.form.name.trim()) reasons.push('Naziv artikla je obavezan.')
      if (!this.form.quantity || this.form.quantity <= 0) reasons.push('Količina mora biti veća od 0.')
      if (!this.form.mpc || this.form.mpc <= 0) reasons.push('Cijena mora biti veća od 0.')
      return reasons.join('\n')
    }
  },

  watch: {
    open (v) { if (v) this.form = this.emptyForm() }
  },

  methods: {
    emptyForm () {
      return { id: uuidv4(), name: '', unit: 'kom', quantity: 1, mpc: 0, vpc: 0, pdv: 0.25, type: 'goods', kpdcode: '' }
    },

    onMpcChange () {
      const rate = this.form.pdv || 0
      this.form.vpc = parseFloat((this.form.mpc / (1 + rate)).toFixed(2))
    },

    onVpcChange () {
      const rate = this.form.pdv || 0
      this.form.mpc = parseFloat((this.form.vpc * (1 + rate)).toFixed(2))
    },

    onPdvChange () {
      if (this.form.mpc > 0) this.onMpcChange()
    },

    onKpdInput (val) {
      if (!val) { this.kpdError = ''; return }
      // Automatski formatiraj: samo znamenke, ubaci točke na pozicijama 2 i 5
      const digits = val.replace(/\D/g, '').slice(0, 6)
      let formatted = digits
      if (digits.length > 4) formatted = digits.slice(0, 2) + '.' + digits.slice(2, 4) + '.' + digits.slice(4)
      else if (digits.length > 2) formatted = digits.slice(0, 2) + '.' + digits.slice(2)
      this.form.kpdcode = formatted
      // Validacija — mora biti xx.xx.xx (6 znamenki)
      if (formatted && !/^\d{2}\.\d{2}\.\d{2}$/.test(formatted)) {
        this.kpdError = 'Format mora biti xx.xx.xx (samo znamenke)'
      } else {
        this.kpdError = ''
      }
    },

    onConfirm () {
      if (!this.form.name || !this.form.unit) return
      if (this.form.kpdcode && this.kpdError) return
      const mpcCents = Math.round(this.form.mpc * 100)
      const qty = this.form.quantity || 1
      this.$emit('add-item', {
        id: this.form.id,
        name: this.form.name,
        quantity: qty,
        unitPrice: mpcCents,
        price: mpcCents,
        total: Math.round(mpcCents * qty),
        unit: this.form.unit,
        selling_unit: this.form.unit,
        code: '',
        type: this.form.type === 'service' ? 'service' : 'product',
        tax: { rate: this.form.pdv, name: `PDV ${Math.round(this.form.pdv * 100)}%` },
        prices: {
          [this.currency]: {
            price: mpcCents,
            taxes: [{ rate: this.form.pdv * 10000, name: `PDV ${Math.round(this.form.pdv * 100)}%` }]
          }
        },
        discount: null,
        note: '',
        manual_price_input: true,
        kpdcode: this.form.kpdcode || ''
      })
      this.$emit('close')
    }
  }
}
</script>

<style scoped>
.modal-header {
  display: flex; align-items: center;
  padding: 18px 20px 14px; border-bottom: 1px solid #F1F5F9;
}
.modal-title { font-size: 15px; font-weight: 700; color: #1e293b; }
.modal-close {
  background: transparent; border: none; cursor: pointer;
  color: #B0BEC5; display: flex; align-items: center;
  padding: 4px; border-radius: 6px; transition: all 0.15s;
}
.modal-close:hover { background: #F1F5F9; color: #374151; }

.modal-body { padding: 18px 20px 10px; }

/* Input stil */
.wi-input >>> .v-input__slot { border-radius: 8px !important; background: #F8FAFC !important; }
.wi-input >>> fieldset { border-color: #E2E8F0 !important; border-radius: 8px !important; }
.wi-input >>> .v-input__slot:hover fieldset { border-color: #00BCD4 !important; }
.wi-input >>> input[type=number]::-webkit-outer-spin-button,
.wi-input >>> input[type=number]::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }
.wi-input >>> input[type=number] { -moz-appearance: textfield; }

/* Vrsta — segmented control */
.wi-type-row {
  display: flex; align-items: center; gap: 12px;
  margin-top: 12px; padding-top: 12px;
  border-top: 1px solid #F1F5F9;
}
.wi-type-label { font-size: 11px; font-weight: 700; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.6px; }
.seg-control { display: flex; background: #F1F5F9; border-radius: 10px; padding: 3px; gap: 2px; }
.seg-btn {
  display: flex; align-items: center;
  padding: 7px 14px; border: none; border-radius: 8px;
  background: transparent; font-size: 12px; font-weight: 600; color: #64748B;
  cursor: pointer; transition: all 0.18s;
}
.seg-btn:hover:not(.active) { color: #334155; }
.seg-btn.active {
  background: #ffffff; color: #00BCD4; font-weight: 700;
  box-shadow: 0 1px 4px rgba(0,0,0,0.10), 0 0 0 1px rgba(0,188,212,0.15);
}

.modal-footer {
  display: flex; align-items: center; justify-content: flex-end;
  gap: 10px; padding: 14px 20px 18px; border-top: 1px solid #F1F5F9;
}
.btn-cancel {
  padding: 10px 20px; background: #F8FAFC;
  border: 1.5px solid #E2E8F0; border-radius: 10px;
  font-size: 13px; font-weight: 600; color: #64748B;
  cursor: pointer; transition: all 0.15s;
}
.btn-cancel:hover { background: #F1F5F9; }
.btn-confirm {
  display: flex; align-items: center; gap: 6px;
  padding: 10px 22px;
  background-color: #00BCD4 !important; background: #00BCD4 !important;
  color: #fff !important; border-radius: 10px; border: none !important;
  font-size: 13px; font-weight: 700; cursor: pointer;
  box-shadow: 0 2px 8px rgba(0,188,212,0.3); transition: background-color 0.15s;
}
.btn-confirm:hover:not(:disabled) { background-color: #0097A7 !important; background: #0097A7 !important; }
.btn-confirm:disabled { opacity: 0.4; cursor: not-allowed; box-shadow: none; }
.wi-tooltip { background: #ffffff !important; box-shadow: 0 4px 16px rgba(0,0,0,0.10) !important; border: 1.5px solid #00BCD4 !important; border-radius: 10px !important; padding: 0 !important; }
</style>
